import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Users, BarChart3, Store, LogOut, Loader2, AlertTriangle,
  RefreshCw, LifeBuoy, QrCode as QrIcon, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, logout } from "../lib/api";
import { LogoMark } from "../components/Logo";
import DashboardHeader from "../components/DashboardHeader";
import RestoreBanner from "../components/RestoreBanner";
import { useDashboardI18n } from "../lib/useDashboardI18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { QRCodeSVG } from "qrcode.react";

// 4-tab spec: Overview, Customers, Analytics, Profile.  Labels resolved
// per-render so the EN/ES toggle in the header repaints instantly.
const NAV = [
  { to: "/business",           icon: LayoutDashboard, key: "tab_overview",  end: true },
  { to: "/business/guest-book", icon: Users,           key: "tab_customers" },
  { to: "/business/analytics",  icon: BarChart3,       key: "tab_analytics" },
  { to: "/business/profile",    icon: Store,           key: "tab_my_business" },
];

export default function BusinessLayout() {
  const { t, lang, setLang } = useDashboardI18n("business");
  const [me, setMe] = useState(null);
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inactivity, setInactivity] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [reapply, setReapply] = useState(null);
  const navigate = useNavigate();

  const loadInactivity = () => {
    api.get("/business/me/inactivity").then(({ data }) => setInactivity(data)).catch(() => {});
  };

  const loadDashboard = async () => {
    setError(null);
    setReapply(null);
    setLoading(true);
    try {
      const meRes = await api.get("/auth/me");
      if (!meRes.data) { navigate("/", { replace: true }); return; }
      setMe(meRes.data);
      if (meRes.data.role !== "business" && meRes.data.role !== "admin") {
        setError("This dashboard is for business accounts. Contact an administrator to upgrade.");
        setLoading(false); return;
      }
      const bizRes = await api.get("/business/me");
      setBiz(bizRes.data);
      loadInactivity();
    } catch (e) {
      if (e.response?.status === 404) {
        const d = e.response?.data?.detail;
        if (d && typeof d === "object" && d.code === "business_not_found") {
          setReapply(d);
        } else {
          setError("We could not find your business listing. This may be a temporary issue.");
        }
      } else if (e.response?.status === 401) { navigate("/", { replace: true }); return; }
      else setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadDashboard(); /* eslint-disable-next-line */ }, [navigate]);

  const retry = async () => {
    setRetrying(true);
    try { await loadDashboard(); }
    finally { setRetrying(false); }
  };

  const doLogout = async () => { await logout(); navigate("/", { replace: true }); };

  if (loading) {
    return <div className="min-h-screen grid place-items-center" data-testid="business-loading"
                style={{ background: "#0B0B0B" }}><Loader2 className="w-6 h-6 animate-spin text-gold" strokeWidth={1.5}/></div>;
  }

  if (reapply) {
    const message = lang === "es" ? reapply.message_es : reapply.message_en;
    const heading = lang === "es" ? "Perfil de negocio no encontrado" : "Business profile not found";
    const cta = lang === "es" ? "Volver a aplicar" : "Reapply";
    const back = lang === "es" ? "Volver al inicio" : "Back to app";
    const signout = lang === "es" ? "Cerrar sesión" : "Sign out";
    return (
      <div className="min-h-screen grid place-items-center px-6" data-testid="business-reapply" style={{ background: "#0B0B0B" }}>
        <div className="lux-card max-w-md p-8 text-center" style={{ background: "#171717" }}>
          <span className="mx-auto w-12 h-12 grid place-items-center rounded-full border border-gold-soft text-gold">
            <AlertTriangle className="w-5 h-5" strokeWidth={1.5}/>
          </span>
          <div className="font-display mt-4 text-foreground" style={{ fontSize: 24 }}>{heading}</div>
          <div className="text-foreground/70 mt-3" style={{ fontSize: 16, lineHeight: 1.5 }}>{message}</div>
          <div className="mt-7 flex flex-col items-stretch gap-3">
            <Link
              to={reapply.reapply_path || "/get-listed/business"}
              data-testid="business-reapply-btn"
              className="btn-gold py-3 px-5 inline-flex items-center justify-center gap-2"
              style={{ minHeight: 48, fontSize: 15 }}
            >
              {cta}
            </Link>
            <div className="flex items-center justify-center gap-4 text-[12px] text-foreground/50 pt-2 border-t border-gold-soft/20">
              <Link to="/" className="hover:text-foreground" data-testid="business-reapply-back">{back}</Link>
              <span>·</span>
              <button onClick={doLogout} className="hover:text-foreground" data-testid="business-reapply-logout">{signout}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center px-6" data-testid="business-error" style={{ background: "#0B0B0B" }}>
        <div className="lux-card max-w-md p-8 text-center" style={{ background: "#171717" }}>
          <span className="mx-auto w-12 h-12 grid place-items-center rounded-full border border-gold-soft text-amber-300">
            <AlertTriangle className="w-5 h-5" strokeWidth={1.5}/>
          </span>
          <div className="font-display text-2xl mt-4 text-foreground">Business dashboard unavailable</div>
          <div className="text-sm text-foreground/65 mt-2">{error}</div>
          <div className="mt-6 flex flex-col items-stretch gap-3">
            <button
              onClick={retry}
              disabled={retrying}
              data-testid="business-retry"
              className="btn-gold py-3 px-5 text-xs inline-flex items-center justify-center gap-2"
              style={{ minHeight: 48 }}
            >
              {retrying ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5}/> : <RefreshCw className="w-4 h-4" strokeWidth={1.5}/>}
              Try again
            </button>
            <Link
              to="/support/tickets"
              data-testid="business-contact-support"
              className="text-xs text-foreground/65 hover:text-gold inline-flex items-center justify-center gap-1.5"
              style={{ minHeight: 44 }}
            >
              <LifeBuoy className="w-3.5 h-3.5" strokeWidth={1.5}/> Contact support
            </Link>
            <div className="flex items-center justify-center gap-4 text-[11px] text-foreground/45 pt-2 border-t border-gold-soft/20">
              <Link to="/" className="hover:text-foreground" data-testid="business-back-home">Back to app</Link>
              <span>·</span>
              <button onClick={doLogout} className="hover:text-foreground" data-testid="business-logout">
                {t("nav_signout")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const openQr = async () => {
    if (!qrData) {
      try {
        const { data } = await api.get("/business/me/qr");
        setQrData(data);
      } catch { toast.error("Couldn't load QR"); return; }
    }
    setQrOpen(true);
  };

  const initials = (biz?.name || "B").trim().slice(0, 2).toUpperCase();
  const avatarUrl = (biz?.photos || [])[0]?.thumbnail
    ? `${process.env.REACT_APP_BACKEND_URL}${biz.photos[0].thumbnail}` : null;

  return (
    <div className="min-h-screen flex" style={{ background: "#0B0B0B", color: "#F5F1E8" }} data-testid="business-dashboard-shell">
      {/* Desktop sidebar — preserved for admin web UX */}
      <aside className="hidden md:flex md:flex-col w-64 shrink-0 p-5"
             style={{ borderRight: "0.5px solid #1a1a1a" }}>
        <Link to="/business" className="flex items-center gap-3 mb-8">
          <LogoMark iconOnly />
          <div>
            <div className="font-display text-lg text-foreground leading-none">{biz?.name}</div>
            <div className="eyebrow mt-1">Business · §3</div>
          </div>
        </Link>
        <nav className="space-y-1 flex-1" data-testid="business-nav">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.key}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive ? "bg-gold/10 text-gold border border-gold-soft" : "text-foreground/70 hover:text-foreground border border-transparent"
                }`}>
                <Icon className="w-4 h-4" strokeWidth={1.5}/>
                <span>{t(n.key)}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="mt-6 lux-card p-3">
          <div className="eyebrow">Signed in as</div>
          <div className="text-xs text-foreground/65 truncate mt-1">{me?.email}</div>
          <button onClick={doLogout} className="mt-3 btn-outline py-1.5 px-3 text-[11px] w-full" data-testid="business-logout">
            <LogOut className="w-3 h-3" strokeWidth={1.5}/> {t("nav_signout")}
          </button>
        </div>
      </aside>

      {/* Mobile header — shared DashboardHeader component */}
      <DashboardHeader
        kind="business"
        ownerName={biz?.name}
        ownerAvatarUrl={avatarUrl}
        overviewPath="/business"
        profilePath="/business/profile"
        onOpenQr={openQr}
      />

      {/* Mobile reactivation pill — sits directly below the header per spec */}
      {inactivity && inactivity.inactive && (
        <div className="md:hidden fixed left-0 right-0 z-30 px-4 pt-2 pb-2"
             style={{ top: 65, background: "#0d0d0d" }}>
          <button
            type="button"
            data-testid="business-header-reactivate"
            onClick={async () => {
              try { await api.post("/business/me/reactivate"); loadInactivity(); toast.success("Listing reactivated"); }
              catch (e) { toast.error(e.response?.data?.detail || e.message); }
            }}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-full"
            style={{ background: "#E8B84B", color: "#0B0B0B", minHeight: 48,
                     fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600 }}
          >
            <RefreshCw className="w-4 h-4" strokeWidth={1.5}/> {t("reactivate_listing")}
          </button>
        </div>
      )}

      {/* QR modal — opened from the header dropdown */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent data-testid="business-qr-modal" style={{ background: "#171717" }}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Your Business QR</DialogTitle>
            <DialogDescription>Customers scan this to open your profile.</DialogDescription>
          </DialogHeader>
          {qrData && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="p-4 bg-white rounded-md">
                <QRCodeSVG value={qrData.qr_data} size={220} level="M" />
              </div>
              <div className="text-xs font-mono text-foreground/55 break-all text-center max-w-xs">{qrData.qr_data}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <main className="flex-1 min-w-0 md:p-8 px-4 py-6 pt-20 md:pt-8 pb-28 md:pb-8">
        <RestoreBanner kind="business" />
        <Outlet context={{ biz, setBiz, me }} />

        {/* Mobile bottom nav per spec */}
        <nav
          className="md:hidden fixed bottom-0 inset-x-0 z-40 grid grid-cols-4"
          style={{ background: "#0d0d0d", borderTop: "0.5px solid #1a1a1a", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          data-testid="business-bottom-nav"
        >
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`mobile-nav-${n.key}`}
                style={({ isActive }) => ({
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                  paddingTop: 10, paddingBottom: 8,
                  color: isActive ? "#E8B84B" : "#9A9490",
                  borderTop: isActive ? "1.5px solid #E8B84B" : "1.5px solid transparent",
                  fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
                  fontFamily: "'DM Sans', sans-serif", minHeight: 48,
                })}>
                <Icon className="w-5 h-5" strokeWidth={1.5}/>
                <span>{t(n.key)}</span>
              </NavLink>
            );
          })}
        </nav>
      </main>
    </div>
  );
}
